import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useSessionStore } from '../../stores/sessionStore';
import { getSessionAuthActions, isSessionDead, renewSession } from '../../auth/sessionRenewal';

function minutesText(minutes: number) {
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * A small notice above the bottom bar, shown only when the user is about to
 * lose access for real: the later of the access token's end and Keycloak's
 * session end (auth/useSessionLifetime.ts works it out).
 *
 * Anyone using the site is kept signed in without seeing this: mouse, keyboard
 * or touch renews the sign-in ahead of time. So it only reaches someone who has
 * left the screen alone, or whose sign-in has hit its maximum:
 * - access ends within 5 minutes and renewing can still help: "I'm still
 *   here" renews (so does moving the mouse, which clears it);
 * - nothing can extend it (session maximum, or Keycloak refused a renewal):
 *   save your work and sign in again;
 * - access has ended: save what you can, then sign in again. A request that
 *   fails then opens the session-expired dialog.
 *
 * It used to be a full-width bar over the top toolbar that read "Your sign-in
 * is ending now. [Stay signed in]" 30 minutes into a background tab. That was
 * Keycloak's idle session ending, while the site kept working on a 48-hour
 * access token, so nobody could tell why it was there.
 *
 * Signing in again leaves the page, so it asks for confirmation first.
 */
export default function SessionExpiryWarning() {
  const isSessionExpiring = useSessionStore((s) => s.isSessionExpiring);
  const minutesRemaining = useSessionStore((s) => s.minutesRemaining);
  const sessionEndsAt = useSessionStore((s) => s.sessionEndsAt);
  const canExtend = useSessionStore((s) => s.canExtend);
  const renewalFailed = useSessionStore((s) => s.renewalFailed);
  const dismissedEndsAt = useSessionStore((s) => s.dismissedEndsAt);
  const dismiss = useSessionStore((s) => s.dismissSessionExpiring);

  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Shown only when access really ends soon (useSessionLifetime works that
  // out: the later of the access token and Keycloak's session). A refused
  // renewal alone is not shown: the access token keeps working until then.
  const ending = isSessionExpiring && (sessionEndsAt === null || sessionEndsAt !== dismissedEndsAt);
  if (!ending) return null;

  const signedOut = minutesRemaining <= 0;
  const mustSignIn = signedOut || !canExtend || renewalFailed;

  const stillHere = async () => {
    setBusy(true);
    setNote(null);
    const ok = await renewSession('user');
    setBusy(false);
    // Dead: the store flips this to "signed out". Otherwise it could not reach Keycloak.
    if (!ok && !isSessionDead()) setNote("Couldn't reach the server. Try again in a moment.");
  };

  const signInNow = () => {
    const actions = getSessionAuthActions();
    if (actions) void actions.signinRedirect();
    else window.location.href = '/';
  };

  let message: string;
  if (confirming) {
    message = "Signing in again leaves this page. Changes you haven't saved will be lost.";
  } else if (signedOut) {
    message = "You've been signed out. Save anything you're working on, then sign in again.";
  } else if (!mustSignIn) {
    message = `You'll be signed out in ${minutesText(minutesRemaining)} because there's been no activity.`;
  } else {
    message = `For security, you'll be signed out in ${minutesText(minutesRemaining)}. Save your work, then sign in again.`;
  }

  const buttonSx = { fontWeight: 700, minWidth: 'auto', textTransform: 'none', fontSize: 'var(--font-size-base)' };

  return (
    <Alert
      severity="warning"
      // Signed out cannot be closed away: the user has to sign in again.
      onClose={signedOut || confirming ? undefined : dismiss}
      sx={{
        position: 'fixed',
        // Above the bottom toolbar (desktop) and the tab bar (phone), clear of
        // the top toolbar and the page's own header controls.
        bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'max-content',
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 2000,
        borderRadius: 2,
        boxShadow: 4,
        alignItems: 'center',
        fontSize: 'var(--font-size-base)',
      }}
    >
      <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <span>{message}</span>
        {note && !confirming && <span>{note}</span>}
        {confirming ? (
          <>
            <Button color="inherit" size="small" variant="outlined" onClick={signInNow} sx={buttonSx}>
              Sign in now
            </Button>
            <Button color="inherit" size="small" onClick={() => setConfirming(false)} sx={buttonSx}>
              Cancel
            </Button>
          </>
        ) : mustSignIn ? (
          <Button color="inherit" size="small" variant="outlined" onClick={() => setConfirming(true)} sx={buttonSx}>
            Sign in again
          </Button>
        ) : (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={() => void stillHere()}
            disabled={busy}
            sx={buttonSx}
          >
            {busy ? 'One moment...' : "I'm still here"}
          </Button>
        )}
      </Box>
    </Alert>
  );
}
